import _typeof from "@babel/runtime/helpers/typeof";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { tokensMap } from '@atlaskit/primitives';

// LiteralUnion technique

// use configured prop value mapping for `prop: { supportedValues: [...] }`

var safeSelectors = /^@media .*$|^::?.*$|^@supports .*$/;
var REX_CSS_SIMPLE_STRING_PROP_VALUE = /^[a-zA-Z-]+$/;
var REX_CSS_WIDTH_PROP_VALUE = /^-?\d+(\.\d+)?(px|em|rem|%)?$/;

// Note: we currently support only free formed size related props (e.g. width).
//       the validation logic should be extended to support other props in the future.
var defaultPropValidator = function defaultPropValidator(rawValue) {
  var value = "".concat(rawValue !== null && rawValue !== void 0 ? rawValue : '').trim();
  return REX_CSS_SIMPLE_STRING_PROP_VALUE.test(value) || REX_CSS_WIDTH_PROP_VALUE.test(value);
};
var _xcssValidateAndCleanup = function xcssValidateAndCleanup(styleObj, supportedXCSSPropsSpec) {
  var isRoot = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  return Object.entries(styleObj).reduce(function (targetStyleObj, _ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      key = _ref2[0],
      value = _ref2[1];
    if (_typeof(value) === 'object' && safeSelectors.test(key) && isRoot) {
      // handle the case where the value is a media query or pseudo selector
      var cleanedValue = _xcssValidateAndCleanup(value, supportedXCSSPropsSpec, false);
      if (cleanedValue && Object.keys(cleanedValue).length > 0) {
        targetStyleObj[key] = cleanedValue;
      }
    } else if (value) {
      var _supportedXCSSPropsSp;
      if ((_supportedXCSSPropsSp = supportedXCSSPropsSpec[key]) !== null && _supportedXCSSPropsSp !== void 0 && _supportedXCSSPropsSp.call(supportedXCSSPropsSpec, value)) {
        // handle the case where the value is a design token value of a token prop
        // or a string value of a standard css prop that passes the validator function
        // if defined.
        targetStyleObj[key] = value;
      } else {
        // eslint-disable-next-line no-console
        console.warn("Unexpected XCSS property provided: ".concat(key, ": ").concat(value));
      }
    } else {
      // in case the prop is not supported.
      // eslint-disable-next-line no-console
      console.warn("Unsupported XCSS property provided: ".concat(key, ": ").concat(value));
    }
    return targetStyleObj;
  }, {});
};
var isSupportedValuesConfig = function isSupportedValuesConfig(config) {
  return config !== true && 'supportedValues' in config && Array.isArray(config.supportedValues);
};

/**
 *
 * @param supportedXCSSProps - the list of css props to be supported for the intended component.
 *    If not provided, all the props will be supported. The props could be either standard css props
 *    or design token based props. If the prop is a design token based prop, the value of the prop
 *    will be validated against the design tokens map to ensure the value is a valid design token string.
 * @returns a function that takes a style object and returns a style object with only the supported props
 *    as specified in the supportedXCSSProps list. The props that are not supported will be removed from the
 *    returned style object and a warning will be logged in the console.
 */
var makeXCSSValidator = function makeXCSSValidator(supportedXCSSProps) {
  var supportedXCSSPropsSpec = Object.entries(supportedXCSSProps).reduce(function (acc, _ref3) {
    var _ref4 = _slicedToArray(_ref3, 2),
      prop = _ref4[0],
      config = _ref4[1];
    var validator;
    if (config === true) {
      var tokensMapStyle = tokensMap[prop];
      if (tokensMapStyle) {
        var tokenSet = new Set(Object.keys(tokensMapStyle));
        validator = function validator(value) {
          return tokenSet.has(value);
        };
      } else {
        throw new Error('invalid validation config applied to token only props');
      }
    } else if (isSupportedValuesConfig(config)) {
      var supportedValues = new Set(config.supportedValues);
      validator = function validator(value) {
        return supportedValues.has(value);
      };
    } else if ((config === null || config === void 0 ? void 0 : config.allowCSS) === true) {
      validator = defaultPropValidator;
    }
    if (validator) {
      acc[prop] = validator;
    }
    return acc;
  }, {});
  return function (styleObj) {
    return _xcssValidateAndCleanup(styleObj, supportedXCSSPropsSpec);
  };
};
export { makeXCSSValidator };